import {getGraveLevelTextByValue, getQueueStatusTextByValue, getTerminalOrderStatusTextByValue} from "./libraryUtility";
import {isNullOrUndefined, isTrimmedStringEmpty} from "../../Utilities/commonUtilities";
import {getLoanStatusTextByValue} from "../Loan/Textbooks/loanUtility";
import {isNumber} from "lodash";
import {SYSTEM_TYPES} from "./libraryConstants";
import {formatDate} from "@containers/Library/Braillers/BraillerLoanManagement/braillerLoanManagementUtility";
import {isObjectNullOrEmpty} from "@utilities/Types/objectUtilities";

export const resourceModel = ({
                                  accessNumber = "",
                                  author = "",
                                  catalogId,
                                  copyright,
                                  description = "",
                                  formats,
                                  gradeLevel,
                                  isbn10,
                                  isbn13,
                                  isActive = true,
                                  numberOfCopiesToAdd = 0,
                                  previewIsbn,
                                  publisher = "",
                                  publisherId,
                                  systemType,
                                  title = "",
                                  whatsNewEndDate,
                                  whatsNewStartDate,
                              }: Record<string, any> = {}) => ({
    accessNumber: accessNumber.toString().padStart(6, "0"),
    author,
    catalogId,
    copyright,
    description,
    formats,
    gradeLevel: gradeLevel === "" ? undefined : gradeLevel,
    isbn10,
    isbn13,
    isActive,
    numberOfCopiesToAdd,
    previewIsbn,
    publisher,
    publisherId,
    systemType,
    title,
    whatsNewEndDate,
    whatsNewStartDate,
});

const testResource = {
    author: "Test Author",
    copyright: "01/06/1981",
    description: "test description",
    gradeLevel: "2",
    isbn10: "1234567890",
    isbn13: "1234567890123",
    title: "test title",
};
export const testResourceModel = resourceModel(testResource);
export const emptyResourceModel = resourceModel();

export const formatModel = ({
                                availability = "",
                                catalogFormatId = "",
                                copies = [],
                                cost = 0,
                                format = 0,
                                formatName = "",
                                fundingSource = 0,
                                numberOfCopiesAvailable = 0,
                                numberOfCopiesToAdd = 0,
                                numberOfVolumes,
                                purchaseOrderNumber = "",
                                shelfLocation = "",
                                vendor = "",
                                vendorId,
                            }: Record<string, any> = {}) => ({
    availability,
    catalogFormatId,
    copies,
    cost,
    format,
    formatName,
    fundingSource,
    numberOfCopiesAvailable,
    numberOfCopiesToAdd,
    numberOfVolumes: numberOfVolumes || 1,
    purchaseOrderNumber,
    shelfLocation,
    vendor,
    vendorId,
});

export const publisherModel = ({
                                   publisherId,
                                   name,
                               }: Record<string, any> = {}) => ({
    publisherId,
    name,
});
const testPublisher = {
    name: "Test Publisher",
};
export const emptyPublisherModel = publisherModel();
export const testPublisherModel = publisherModel(testPublisher);


export const vendorModel = ({
                                vendorId,
                                name,
                                email,
                                contactName,
                                phoneNumber,
                                faxNumber,
                                address,
                                city,
                                state,
                                zipCode,
                                website,
                                systemType,
                            }: Record<string, any> = {}) => ({
    vendorId,
    name,
    email,
    contactName,
    phoneNumber,
    faxNumber,
    address,
    city,
    state,
    zipCode,
    website,
    systemType,
});
const testVendor = {
    name: "Test Vendor",
    email: "Vendor@vending.com",
    contactName: "Contact Vendor",
    phoneNumber: "123-456-7890",
    faxNumber: "098-765-4321",
    address: "123 Vending St",
    city: "Vendoropolis",
    state: "VN",
    zipCode: "12345",
    website: "www.vending.com",
    systemType: SYSTEM_TYPES.Textbooks,
};
export const emptyVendorModel = vendorModel({ systemType: SYSTEM_TYPES.Textbooks});
export const testVendorModel = vendorModel(testVendor);


function returnAuthor(author: string) {
    const hasAuthor = !isTrimmedStringEmpty(author);
    return hasAuthor ? `<div><b>${author}</b> (Author)</div>` : "";
}

function returnCatalogFormat(catalogFormat: string) {
    const hasFormat = !isTrimmedStringEmpty(catalogFormat);
    return hasFormat ? `<div><b>${catalogFormat}</b> (Format)</div>` : "";
}

function returnGradeLevel(gradeLevel: number) {
    const hasGradeLevel = isNumber(gradeLevel) && gradeLevel > 0;
    return hasGradeLevel ? `<div><b>${getGraveLevelTextByValue(gradeLevel)}</b> (Grade)` : "";
}

function returnIsbn10(isbn10: string) {
    const hasIsbn10 = !isTrimmedStringEmpty(isbn10);
    return hasIsbn10 ? `<div><b>${isbn10}</b> (ISBN-10)</div>` : "";
}

function returnIsbn13(isbn13: string) {
    const hasIsbn13 = !isTrimmedStringEmpty(isbn13);
    return hasIsbn13 ? `<div><b>${isbn13}</b> (ISBN-13)</div>` : "";
}

function returnPreviewIsbn(previewIsbn: string) {
    const hasPreviewIsbn = !isTrimmedStringEmpty(previewIsbn);
    return hasPreviewIsbn ? `<div><b>${previewIsbn}</b> (Preview ISBN)</div>` : "";
}

function returnQueueStatus(queueStatus: number) {
    const hasStatus = !isNullOrUndefined(queueStatus);
    return hasStatus ? `<div><b>${getQueueStatusTextByValue(queueStatus)}</b> (Status)</div>` : "";
}

function returnOrderStatus(orderStatus: string | number) {
    const hasOrderStatus = !isNullOrUndefined(orderStatus);
    if (!hasOrderStatus) return "";

    const orderStatusText = getTerminalOrderStatusTextByValue(orderStatus);
    if (!orderStatusText) return "";

    return `<div class="warning-color"><b>${orderStatusText}</b> (Order Status)</div>`;
}

function returnStudent(student: Record<string, unknown>) {
    const hasStudent = !isNullOrUndefined(student);
    return hasStudent ? `<div><b>${createStudent(student)}</b> (Student)</div>` : "";
}

function returnAccessNumber(accessNumber: string | number) {
    const hasAccessNumber = !isNullOrUndefined(accessNumber);
    return hasAccessNumber ? `<div><b>${createFormattedAccessNumber(accessNumber)}</b> (Access Number)</div>` : "";
}

function returnPublisher(publisher: Record<string, unknown>) {
    const hasPublisher = !isNullOrUndefined(publisher);
    return hasPublisher ? `<div><b>${publisher.name}</b> (Publisher)</div>` : "";
}

function createFormattedAccessNumber(accessNumber: string | number) {
    return accessNumber.toString().padStart(6, '0');
}

function createVolumeRange(volumes: Record<string, unknown>[] = []) {
    if (isNullOrUndefined(volumes) || volumes.length === 0) return "";

    const volumeNumbers = volumes
        .map(volume => Number(volume?.volumeNumber))
        .filter(volumeNumber => Number.isInteger(volumeNumber) && volumeNumber > 0)
        .sort((a, b) => a - b);

    if (volumeNumbers.length === 0) return "";

    const firstVolume = volumeNumbers[0].toString().padStart(3, "0");
    const lastVolume = volumeNumbers[volumeNumbers.length - 1].toString().padStart(3, "0");

    return firstVolume === lastVolume
        ? `(${firstVolume})`
        : `(${firstVolume} - ${lastVolume})`;
}

function returnDateRequested(dateRequested: string) {
    const hasDateRequested = !isObjectNullOrEmpty(dateRequested);
    return hasDateRequested ? `<div><b>${formatDate(dateRequested)}</b> (Date Requested)</div>` : "";
}

function createDetails({
                           accessNumber,
                           author,
                           gradeLevel,
                           isbn10,
                           isbn13,
                           previewIsbn,
                           publisher
                       }: Record<string, unknown>) {
    let details = "";

    details += returnAuthor(author as string);
    details += returnGradeLevel(gradeLevel as number);
    details += returnIsbn10(isbn10 as string);
    details += returnIsbn13(isbn13 as string);
    details += returnPreviewIsbn(previewIsbn as string);
    details += returnAccessNumber(accessNumber as string | number);
    details += returnPublisher(publisher as Record<string, unknown>);
    details += !isTrimmedStringEmpty(details) ? `</div><div class="margin--bottom"></div>` : '';
    return details;
}

function createQueueDetails({
                                author,
                                catalogFormat,
                                gradeLevel,
                                queueStatus,
                                student,
                                isbn10,
                                isbn13,
                                dateRequested,
                                orderStatus
                            }: Record<string, unknown>) {
    let details = "";

    details += returnAuthor(author as string);
    details += returnCatalogFormat(catalogFormat as string);
    details += returnGradeLevel(gradeLevel as number);
    details += returnQueueStatus(queueStatus as number);
    details += returnStudent(student as Record<string, unknown>);
    details += returnIsbn10(isbn10 as string);
    details += returnIsbn13(isbn13 as string);
    details += returnDateRequested(dateRequested as string);
    details += returnOrderStatus(orderStatus as number);
    details += !isTrimmedStringEmpty(details) ? `<div class="margin--bottom"></div>` : '';
    return details;
}

export function createShipping({patron, shippingLocation}: Record<string, unknown>) {
    let shipping = "";

    const hasPatron = !isNullOrUndefined(patron);
    const hasShippingLocation = !isNullOrUndefined(shippingLocation);

    shipping += hasPatron ? `<div>${createPatron(patron as Record<string, unknown>)}</div>` : "";
    shipping += hasShippingLocation ? `<div>${createShippingLocation(shippingLocation as Record<string, unknown>)}</div>` : "";
    shipping += hasPatron || hasShippingLocation ? `<div class="margin--bottom"></div>` : '';
    return shipping;
}

function createStudent(student: Record<string, unknown>) {
    return `${student.firstName || ""} ${student.middleName || ""} ${student.lastName || ""}`;
}

function createPatron(patron: Record<string, unknown>) {
    return `C/O: ${patron.firstName || ""} ${patron.middleName || ""} ${patron.lastName || ""}`;
}

function createShippingLocation(shippingLocation: Record<string, unknown>) {
    let location = "";
    location += `<div>${shippingLocation.name}</div>`;
    location += `<div>${shippingLocation.address}</div>`;
    location += `<div>${shippingLocation.city}, ${shippingLocation.state} ${shippingLocation.zipCode}</div>`;
    return location;
}

export const catalogItemModel = ({
                                     accessNumber,
                                     author,
                                     catalogId,
                                     description,
                                     formats,
                                     gradeLevel,
                                     title,
                                     isbn10,
                                     isbn13,
                                     previewIsbn,
                                     publisher
                                 }: Record<string, any> = {}) => ({
    catalogId,
    description: description ? `<p>${description}</p>` : "",
    details: createDetails({accessNumber, author, gradeLevel, isbn10, isbn13, previewIsbn, publisher}),
    formats,
    title,
});


export const catalogQueueModel = ({
                                      author,
                                      catalogId,
                                      catalogFormat,
                                      gradeLevel,
                                      orderId,
                                      orderStatus,
                                      orderType,
                                      queueStatus,
                                      student,
                                      title,
                                      isbn10,
                                      isbn13,
                                      dateRequested,
                                  }: Record<string, any> = {}) => ({
    catalogId,
    details: createQueueDetails({
        author,
        catalogFormat,
        gradeLevel,
        queueStatus,
        student,
        isbn10,
        isbn13,
        dateRequested,
        orderStatus,
    }),
    orderId,
    orderStatus,
    orderType,
    title,
});

export const studentQueueModel = ({
                                      confirmDuplicateQueueItems = false,
                                      needFrom,
                                      needTo,
                                      queueItems,
                                      shippingLocationId = "",
                                      studentId = "",
                                  }: Record<string, any> = {}) => ({
    confirmDuplicateQueueItems,
    needFrom,
    needTo,
    queueItems,
    shippingLocationId,
    studentId,
});
export const emptyStudentQueueModel = studentQueueModel();

export const loanQueueRow = ({
                                 dateRequested,
                                 formatName = "",
                                 loanStatus,
                                 patron,
                                 queueItemId,
                                 shippingLocation,
                                 student,
                                 title,
                                 needFromFormatted,
                                 barCode,
                                 shelfLocation,
                                 volumes = []
                             }: Record<string, any> = {}) => ({

    barCode: barCode,
    dateRequested,
    loanStatus: loanStatus || 0,
    loanStatusText: getLoanStatusTextByValue(loanStatus || 0),
    patron: patron?.fullName,
    queueItemId,
    shelfLocation: shelfLocation,
    shippingLocation: shippingLocation?.name,
    student: student?.fullName,
    title: `${title} - ${formatName}`,
    volumeRange: createVolumeRange(volumes),
    needFromFormatted,
});
